/**
 * Agent policies authored in TrustLogix with the rule builder: one policy per
 * agent, made of independent rules, each targeting explicit tools on ONE MCP
 * server with its own effect and conditions. Tool customizations ("Customize
 * Tool Access" / "Edit Tool Rules") are the same object - a policy whose rule
 * targets one tool - so there is one model and one editor.
 *
 * The UI's model, which the backend is moving to (TRUST-13500 comment 58796).
 * Live calls go through `AuthoredPolicyApi` (`@/pages/AgentEstate/authoredPolicyApi`).
 */
import type { ConditionEntry } from "@/components/ConditionBuilder/types";
import { hasErrorCode } from "@/helpers/apiErrorDetail";

/**
 * `ALLOW` / `DENY` for agent-level rules. A tool customization may only narrow
 * what the agent inherits, so it offers `DENY` and `ALLOW_ONLY_WHEN` - never a
 * permissive exception, which needs a precedence model the server has not
 * defined.
 */
export type AgentPolicyEffect = "ALLOW" | "DENY" | "ALLOW_ONLY_WHEN";

export const AGENT_POLICY_EFFECTS: readonly AgentPolicyEffect[] = [
  "ALLOW",
  "DENY",
];
export const TOOL_CUSTOMIZATION_EFFECTS: readonly AgentPolicyEffect[] = [
  "DENY",
  "ALLOW_ONLY_WHEN",
];

/**
 * What a rule targets on its server:
 * - `TOOLS`: the explicit `toolIds` and nothing else;
 * - `SERVER`: every tool on the server, including tools added later.
 *   `toolIds` is empty - the server's tool list is resolved at evaluation.
 */
export type AgentPolicyRuleScope = "SERVER" | "TOOLS";

export interface AgentPolicyRule {
  id: string;
  name?: string;
  /** Exactly one MCP server per rule. */
  mcpServerId: string;
  /** Absent = `TOOLS`, so rules saved before server scope still read. */
  scope?: AgentPolicyRuleScope;
  /** Explicit tool ids for a `TOOLS` rule; empty for a `SERVER` rule. */
  toolIds: string[];
  effect: AgentPolicyEffect;
  combinator: "AND" | "OR";
  /** Empty = the rule applies unconditionally to its tools. */
  conditions: ConditionEntry[];
}

export const isServerScopedRule = (rule: Pick<AgentPolicyRule, "scope">) =>
  rule.scope === "SERVER";

/**
 * The tool ids a rule targets today: its explicit ids, or every tool its
 * server currently has. Empty when the agent no longer reaches the server.
 */
export const ruleTargetToolIds = (
  rule: Pick<AgentPolicyRule, "scope" | "mcpServerId" | "toolIds">,
  services: ReadonlyArray<{ id: string; tools: ReadonlyArray<{ id: string }> }>,
): string[] =>
  isServerScopedRule(rule)
    ? (services
        .find(service => service.id === rule.mcpServerId)
        ?.tools.map(tool => tool.id) ?? [])
    : rule.toolIds;

/**
 * Remove Rule takes ONE rule out of its policy - never the policy. A policy's
 * only rule cannot go: that would leave an empty policy, and deleting a policy
 * belongs to policy management.
 */
export const canRemoveRule = ({
  policy,
  rule,
}: {
  policy: Pick<AuthoredAgentPolicy, "rules">;
  rule: Pick<AgentPolicyRule, "id">;
}) => policy.rules.length > 1 && policy.rules.some(item => item.id === rule.id);

export type AgentPolicyOrigin = "AGENT" | "TOOL_CUSTOMIZATION";
export type AgentPolicyStatus = "DRAFT" | "APPLIED";

export interface AuthoredAgentPolicy {
  id: string;
  agentId: string;
  name: string;
  description?: string;
  origin: AgentPolicyOrigin;
  rules: AgentPolicyRule[];
  /** Agent-level policies a tool customization was written against. */
  inheritedPolicyIds: string[];
  /** Created by applying this suggested improvement. */
  recommendationId?: string;
  /**
   * DRAFT is a saved definition only. APPLIED means assigned to the agent -
   * not that enforcement is in place; that is the agent's enforcement setup.
   */
  status: AgentPolicyStatus;
  /** Optimistic-concurrency token: a write naming an older one is rejected. */
  version: number;
  updatedAt: string;
  updatedBy: string;
  appliedAt?: string;
}

/** The authored content a create/update sends. Rule ids are kept on edit. */
export interface AgentPolicyInput {
  name: string;
  description?: string;
  origin: AgentPolicyOrigin;
  inheritedPolicyIds: string[];
  recommendationId?: string;
  rules: Array<Omit<AgentPolicyRule, "id"> & { id?: string }>;
}

export interface AgentPoliciesQuery {
  environmentId: string;
  agentId: string;
}

export interface CreateAgentPolicyRequest extends AgentPoliciesQuery {
  policy: AgentPolicyInput;
  /** APPLIED saves and assigns in one step. */
  status: AgentPolicyStatus;
  actor: string;
  /**
   * The agent's MCP servers as the caller already has them. Live saves a
   * server-wide rule as its server's tools; without these it reads them.
   */
  mcpServices?: ReadonlyArray<{
    id: string;
    tools: ReadonlyArray<{ id: string }>;
  }>;
}

/** Deletes a whole policy - every rule in it. */
export interface DeleteAgentPolicyRequest extends AgentPoliciesQuery {
  policyId: string;
}

export interface UpdateAgentPolicyRequest extends CreateAgentPolicyRequest {
  policyId: string;
  /** The version the author loaded; a mismatch is a conflict, never a merge. */
  version: number;
}

const POLICY_VERSION_CONFLICT = "AGENT_POLICY_VERSION_CONFLICT";

/** The policy changed since the author loaded it. Their edits are kept. */
export class PolicyVersionConflictError extends Error {
  readonly code = POLICY_VERSION_CONFLICT;
  constructor(
    message = "This policy changed since you opened it. Reload and try again.",
  ) {
    super(message);
    this.name = "PolicyVersionConflictError";
  }
}

export const isPolicyVersionConflictError = (error: unknown): boolean =>
  hasErrorCode(error, POLICY_VERSION_CONFLICT);
